import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const schema = z.object({
  full_name: z.string().trim().min(2, "Please enter your full name").max(100),
  email: z.string().trim().email("Enter a valid email").max(255),
  phone: z
    .string()
    .trim()
    .min(10, "Enter a valid phone number")
    .max(20)
    .regex(/^[0-9+\-\s()]+$/, "Only digits and + - ( ) allowed"),
  city: z.string().trim().max(100).optional().or(z.literal("")),
  education: z.string().trim().max(150).optional().or(z.literal("")),
  why_interested: z
    .string()
    .trim()
    .min(20, "Please write at least 20 characters")
    .max(1000, "Keep it under 1000 characters"),
  portfolio_links: z.string().trim().max(500).optional().or(z.literal("")),
});

type FormState = z.infer<typeof schema>;

const initial: FormState = {
  full_name: "",
  email: "",
  phone: "",
  city: "",
  education: "",
  why_interested: "",
  portfolio_links: "",
};

export const InternApplicationForm = () => {
  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (key: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<keyof FormState, string>> = {};
      parsed.error.issues.forEach((i) => {
        const k = i.path[0] as keyof FormState;
        if (!fieldErrors[k]) fieldErrors[k] = i.message;
      });
      setErrors(fieldErrors);
      toast.error("Please fix the errors and try again.");
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.from("intern_applications").insert({
      full_name: parsed.data.full_name,
      email: parsed.data.email,
      phone: parsed.data.phone,
      city: parsed.data.city || null,
      education: parsed.data.education || null,
      why_interested: parsed.data.why_interested,
      portfolio_links: parsed.data.portfolio_links || null,
      status: "new",
    });
    setSubmitting(false);

    if (error) {
      toast.error("Submission failed. Please try again.");
      return;
    }
    setSubmitted(true);
    toast.success("Application submitted!");
    setForm(initial);
  };

  if (submitted) {
    return (
      <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-8 text-center">
        <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-primary" />
        <h3 className="text-xl font-bold text-foreground">Application received</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Thanks for applying! Shortlisted candidates will be contacted by email within 7–10 days.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-5"
          onClick={() => setSubmitted(false)}
        >
          Submit another application
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-5"
      noValidate
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="full_name">Full name *</Label>
          <Input id="full_name" value={form.full_name} onChange={handleChange("full_name")} maxLength={100} />
          {errors.full_name && <p className="mt-1 text-xs text-destructive">{errors.full_name}</p>}
        </div>
        <div>
          <Label htmlFor="email">Email *</Label>
          <Input id="email" type="email" value={form.email} onChange={handleChange("email")} maxLength={255} />
          {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
        </div>
        <div>
          <Label htmlFor="phone">Phone / WhatsApp *</Label>
          <Input id="phone" value={form.phone} onChange={handleChange("phone")} maxLength={20} />
          {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone}</p>}
        </div>
        <div>
          <Label htmlFor="city">City</Label>
          <Input id="city" value={form.city} onChange={handleChange("city")} maxLength={100} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="education">Education / Year of study</Label>
          <Input
            id="education"
            value={form.education}
            onChange={handleChange("education")}
            maxLength={150}
            placeholder="e.g. BCA 2nd year, B.Com graduate, Class 12 passed"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="why_interested">Why are you interested in this role? *</Label>
        <Textarea
          id="why_interested"
          value={form.why_interested}
          onChange={handleChange("why_interested")}
          maxLength={1000}
          rows={4}
          placeholder="A few lines on your interest in digital marketing and what you'd bring to We-Help.in"
        />
        <div className="mt-1 flex justify-between text-xs">
          {errors.why_interested ? (
            <span className="text-destructive">{errors.why_interested}</span>
          ) : (
            <span className="text-muted-foreground">Min 20, max 1000 characters</span>
          )}
          <span className="text-muted-foreground">{form.why_interested.length}/1000</span>
        </div>
      </div>

      <div>
        <Label htmlFor="portfolio_links">Portfolio / Social links (optional)</Label>
        <Textarea
          id="portfolio_links"
          value={form.portfolio_links}
          onChange={handleChange("portfolio_links")}
          maxLength={500}
          rows={2}
          placeholder="Instagram, LinkedIn, Behance, Drive folder, Canva, etc."
        />
      </div>

      <Button type="submit" variant="cta" size="lg" disabled={submitting} className="w-full sm:w-auto">
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
          </>
        ) : (
          <>
            <Send className="h-4 w-4" /> Submit application
          </>
        )}
      </Button>
      <p className="text-xs text-muted-foreground">
        Your details are stored securely and only visible to the We-Help.in admin team.
      </p>
    </form>
  );
};